import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsFeeManagement = ({ onNavigate, userInfo = {} }) => {
    // Accounts User Data State (for Sidebar)
    const [accountsData, setAccountsData] = useState({
        name: "Loading...",
        accountsId: "...",
        department: "Accounts Department",
        designation: "Accounts Officer",
        profileImage: null
    });

    // State for fee data
    const [feeStructures, setFeeStructures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(10);

    // Filter states
    const [filters, setFilters] = useState({
        department: '',
        batch: '',
        year: '',
        semester: ''
    });

    // Dynamic filter data
    const [filterBatches, setFilterBatches] = useState([]);
    const [filterBatchesLoading, setFilterBatchesLoading] = useState(false);

    // Student Data State
    const [allStudents, setAllStudents] = useState([]); // Store ALL students for client-side search
    const [searchTerm, setSearchTerm] = useState(''); // Global student search
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // Debounced search term
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [showManageFeeModal, setShowManageFeeModal] = useState(false);
    const [selectedStudentForFee, setSelectedStudentForFee] = useState(null);
    const [feeStates, setFeeStates] = useState({
        totalFee: '',
        paymentAmount: ''
    });
    const [feeBreakdown, setFeeBreakdown] = useState([]); // State for detailed fee structure
    const [oneTimeFees, setOneTimeFees] = useState({
        admissionFees: { amount: '', isPaid: false },
        uniformKit: { amount: '', isPaid: false },
        skillFees: { amount: '', isPaid: false },
        registrationFees: { amount: '', isPaid: false }
    });
    // Available options for dropdowns - FETCHED FROM DB NOW
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);

    // API HELPER FUNCTION
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                throw new Error('Session expired');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    };

    // FETCH DEPARTMENTS
    const fetchDepartments = async () => {
        try {
            setDepartmentsLoading(true);
            const result = await apiCall('/departments');

            if (result.success && result.data) {
                setDepartments(result.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const semesters = [
        { value: '1st Sem', label: '1st Semester' },
        { value: '2nd Sem', label: '2nd Semester' },
        { value: '3rd Sem', label: '3rd Semester' },
        { value: '4th Sem', label: '4th Semester' },
        { value: '5th Sem', label: '5th Semester' },
        { value: '6th Sem', label: '6th Semester' },
        { value: '7th Sem', label: '7th Semester' },
        { value: '8th Sem', label: '8th Semester' }
    ];

    // Fetch Accounts Dashboard Data for Sidebar User Info
    useEffect(() => {
        const fetchAccountsDashboard = async () => {
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                if (!token) {
                    setAccountsData(prev => ({
                        ...prev,
                        name: userInfo?.name || "Accounts User",
                        accountsId: userInfo?.id || "ACC2024001"
                    }));
                    return;
                }

                const response = await fetch(`${API_LINK}/api/accounts/dashboard`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        const data = result.data;
                        setAccountsData({
                            name: data.accounts?.name || userInfo?.name || "Accounts Officer",
                            accountsId: data.accounts?.accountsId || userInfo?.id || "ACC2024001",
                            department: data.accounts?.department || "Accounts Department",
                            designation: data.accounts?.designation || "Accounts Officer",
                            profileImage: data.accounts?.profileImage || null
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching accounts dashboard data:', error);
            }
        };
        fetchAccountsDashboard();
    }, [userInfo]);


    // Year options for filter
    const yearOptions = [
        { value: 1, label: '1st Year' },
        { value: 2, label: '2nd Year' },
        { value: 3, label: '3rd Year' },
        { value: 4, label: '4th Year' }
    ];

    // FETCH FILTER BATCHES
    const fetchFilterBatches = async (department, year) => {
        if (!department) {
            setFilterBatches([]);
            return;
        }

        try {
            setFilterBatchesLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            let apiUrl = `${API_LINK}/api/admin/batches?department=${encodeURIComponent(department)}`;

            // If year is selected, add it to filters
            if (year) {
                const dbYear = 2000 + parseInt(year);
                apiUrl += `&year=${dbYear}`;
            }

            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (result.success && result.data) {
                setFilterBatches(result.data);
            } else {
                setFilterBatches([]);
            }
        } catch (error) {
            console.error('Error fetching filter batches:', error);
            setFilterBatches([]);
        } finally {
            setFilterBatchesLoading(false);
        }
    };

    // Fetch fee structures from API
    const fetchFeeStructures = async (page = 1, filterParams = {}) => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', limit.toString());

            if (filterParams.department) params.append('department', filterParams.department);
            if (filterParams.batch) params.append('batch', filterParams.batch);
            if (filterParams.semester) params.append('semester', filterParams.semester);
            if (filterParams.year) params.append('year', filterParams.year);

            const apiUrl = `${API_LINK}/api/admin/fees?${params.toString()}`;

            console.log('Fetching fee structures with URL:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) {
                    setError('Session expired. Please login again.');
                    handleLogout();
                    return;
                }
                throw new Error(`Server error: ${response.status} - ${errorText}`);
            }

            const result = await response.json();

            if (result.success) {
                const feeData = result.data?.feeStructures || [];
                const paginationData = result.data?.pagination || {};

                setFeeStructures(feeData);

                if (paginationData.totalCount !== undefined) {
                    setTotalCount(paginationData.totalCount);
                    setTotalPages(paginationData.totalPages || 1);
                    setCurrentPage(paginationData.currentPage || page);
                } else {
                    setTotalCount(feeData.length);
                    setTotalPages(Math.ceil(feeData.length / limit));
                    setCurrentPage(page);
                }

            } else {
                setError(result.message || 'Failed to fetch fee structures from server');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError(`Failed to load fee structures: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch and all students fetch
    useEffect(() => {
        fetchDepartments();

        const timer = setTimeout(() => {
            fetchFeeStructures(1, filters);
        }, 100);

        const fetchAllStudents = async () => {
            try {
                setStudentsLoading(true);
                // Ensure we get Roll No and Reg No by using general endpoint
                const result = await apiCall('/students?limit=1000000');
                if (result.success && result.data && result.data.students) {
                    setAllStudents(result.data.students);
                }
            } catch (err) {
                console.error("Error fetching all students:", err);
            } finally {
                setStudentsLoading(false);
            }
        };
        fetchAllStudents();

        return () => clearTimeout(timer);
    }, []);

    // Debounce search term
    useEffect(() => {
        // We do NOT set loading true here to avoid UI flickering/refreshing effect on every keypress
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(prev => {
                if (prev === searchTerm) {
                    return prev;
                }
                return searchTerm;
            });
        }, 600); // Increased to 800ms for better debounce experience

        return () => clearTimeout(timer);
    }, [searchTerm, debouncedSearchTerm]);

    // Filter Logic that runs when Filters OR SearchTerm OR AllStudents change
    useEffect(() => {
        if (allStudents.length === 0) return;

        let filtered = [...allStudents];

        // 1. Text Search (Priority)
        if (debouncedSearchTerm) {
            const lowerSearch = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(s =>
                (s.name && s.name.toLowerCase().includes(lowerSearch)) ||
                (s.student_name && s.student_name.toLowerCase().includes(lowerSearch)) ||
                (s.student_id && s.student_id.toLowerCase().includes(lowerSearch)) ||
                (s.roll_number && s.roll_number.toLowerCase().includes(lowerSearch)) ||
                (s.reg_number && s.reg_number.toLowerCase().includes(lowerSearch))
            );
        }

        // 2. Dropdown Filters
        if (filters.department) {
            filtered = filtered.filter(s => s.dept === filters.department);
        }
        if (filters.year) {
            filtered = filtered.filter(s => {
                const sYear = parseInt(s.year);
                return sYear === parseInt(filters.year) || (sYear - 2000) === parseInt(filters.year);
            });
        }
        if (filters.batch) {
            filtered = filtered.filter(s => s.batch_name === filters.batch);
        }
        if (filters.semester) {
            filtered = filtered.filter(s => s.semester == filters.semester);
        }

        // Optimization: If NO search term and NO filters, don't show all students
        const hasActiveFilter = filters.department || filters.batch || filters.year || filters.semester || debouncedSearchTerm;

        if (hasActiveFilter) {
            console.log(`Setting students display: ${filtered.length} found`);
            setStudents(filtered);
        } else {
            setStudents([]);
        }
        setStudentsLoading(false);

    }, [allStudents, filters, debouncedSearchTerm]);

    // Handle filter changes
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...filters, [filterName]: value };

        // Reset dependent filters
        if (filterName === 'department') {
            newFilters.batch = '';
            setFilterBatches([]);

            if (value) {
                fetchFilterBatches(value, filters.year);
            }
        } else if (filterName === 'year') {
            if (filters.department) {
                fetchFilterBatches(filters.department, value);
            }
        }

        setFilters(newFilters);
        setCurrentPage(1);
        fetchFeeStructures(1, newFilters);
    };

    // Clear all filters
    const clearFilters = () => {
        const clearedFilters = {
            department: '',
            batch: '',
            year: '',
            semester: ''
        };
        setFilters(clearedFilters);
        setFilterBatches([]);
        setSearchTerm('');
        setCurrentPage(1);
        fetchFeeStructures(1, clearedFilters);
        setStudents([]);
    };

    // State for Payment Confirmation Modal
    const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
    const [currentPaymentSelection, setCurrentPaymentSelection] = useState(null); // { type, key/index, subKey, newValue }
    const [paymentDetailsInput, setPaymentDetailsInput] = useState({
        mode: 'Online', // 'Online' or 'Offline'
        date: new Date().toISOString().split('T')[0],
        transactionId: ''
    });

    // Handle Manage Fee Click
    const handleManageFeeClick = (student) => {
        setSelectedStudentForFee(student);
        setFeeStates({
            totalFee: student.total_fee || student.course_fee || '',
            currentPaid: student.paid_amount || 0,
            paymentAmount: ''
        });

        // Initialize One-Time Fees
        setOneTimeFees({
            admissionFees: { amount: 20000, isPaid: false, paymentDetails: null },
            uniformKit: { amount: 6500, isPaid: false, paymentDetails: null },
            skillFees: { amount: 26800, isPaid: false, paymentDetails: null },
            registrationFees: { amount: 4000, isPaid: false, paymentDetails: null }
        });

        // Initialize Semester-wise dummy data
        const initialBreakdown = Array.from({ length: 8 }, (_, i) => ({
            semester: i + 1,
            semesterFees: { amount: 35000, isPaid: false, paymentDetails: null },
            examFees: { amount: 1000, isPaid: false, paymentDetails: null },
        }));
        setFeeBreakdown(initialBreakdown);
        setShowManageFeeModal(true);
    };

    // Handle Close Manage Fee Modal
    const handleCloseManageFeeModal = () => {
        setShowManageFeeModal(false);
        setSelectedStudentForFee(null);
        setFeeStates({ totalFee: '', currentPaid: '', paymentAmount: '' });
    };

    // Handle Payment Checkbox Click
    const handlePaymentCheckbox = (type, identifier, subKey = null) => {
        let isCurrentlyPaid = false;

        if (type === 'oneTime') {
            isCurrentlyPaid = oneTimeFees[identifier].isPaid;
        } else if (type === 'semester') {
            isCurrentlyPaid = feeBreakdown[identifier][subKey].isPaid;
        }

        const newValue = !isCurrentlyPaid;

        if (newValue) {
            // Marking as PAID -> Open Payment Details Modal
            setCurrentPaymentSelection({ type, identifier, subKey, isEditing: false });
            setPaymentDetailsInput({
                mode: 'Online',
                date: new Date().toISOString().split('T')[0],
                transactionId: ''
            });
            setShowPaymentConfirmModal(true);
        } else {
            // Marking as UNPAID -> Confirm and Remove
            if (window.confirm('Are you sure you want to mark this fee as UNPAID? This will remove payment details.')) {
                if (type === 'oneTime') {
                    setOneTimeFees(prev => ({
                        ...prev,
                        [identifier]: { ...prev[identifier], isPaid: false, paymentDetails: null }
                    }));
                } else if (type === 'semester') {
                    setFeeBreakdown(prev => {
                        const newArr = [...prev];
                        newArr[identifier][subKey] = { ...newArr[identifier][subKey], isPaid: false, paymentDetails: null };
                        return newArr;
                    });
                }
            }
        }
    };

    // Handle View Payment Details (Opens Modal with existing data)
    const handleViewPayment = (type, identifier, subKey = null) => {
        let existingDetails = null;

        if (type === 'oneTime') {
            existingDetails = oneTimeFees[identifier].paymentDetails;
        } else if (type === 'semester') {
            existingDetails = feeBreakdown[identifier][subKey].paymentDetails;
        }

        if (existingDetails) {
            setCurrentPaymentSelection({ type, identifier, subKey, isEditing: true });
            setPaymentDetailsInput({
                mode: existingDetails.mode || 'Online',
                date: existingDetails.date || new Date().toISOString().split('T')[0],
                transactionId: existingDetails.transactionId || ''
            });
            setShowPaymentConfirmModal(true);
        }
    };

    // Confirm Payment and Save Details
    const handleConfirmPayment = () => {
        if (!currentPaymentSelection) return;

        const { type, identifier, subKey } = currentPaymentSelection;
        const details = { ...paymentDetailsInput };

        if (type === 'oneTime') {
            setOneTimeFees(prev => ({
                ...prev,
                [identifier]: { ...prev[identifier], isPaid: true, paymentDetails: details }
            }));
        } else if (type === 'semester') {
            setFeeBreakdown(prev => {
                const newArr = [...prev];
                newArr[identifier][subKey] = { ...newArr[identifier][subKey], isPaid: true, paymentDetails: details };
                return newArr;
            });
        }

        setShowPaymentConfirmModal(false);
        setCurrentPaymentSelection(null);
    };

    // Remove Payment Details (Unpay)
    const handleRemovePayment = () => {
        if (!currentPaymentSelection) return;

        if (window.confirm('Are you sure you want to remove this payment? The fee will be marked as Unpaid.')) {
            const { type, identifier, subKey } = currentPaymentSelection;

            if (type === 'oneTime') {
                setOneTimeFees(prev => ({
                    ...prev,
                    [identifier]: { ...prev[identifier], isPaid: false, paymentDetails: null }
                }));
            } else if (type === 'semester') {
                setFeeBreakdown(prev => {
                    const newArr = [...prev];
                    newArr[identifier][subKey] = { ...newArr[identifier][subKey], isPaid: false, paymentDetails: null };
                    return newArr;
                });
            }
            setShowPaymentConfirmModal(false);
            setCurrentPaymentSelection(null);
        }
    };

    // Handle Save Fee (Final Save)
    const handleSaveFee = async (e) => {
        e.preventDefault();
        // In a real app, you would send this to the backend with all paymentDetails
        console.log('Saving Fees:', { oneTimeFees, feeBreakdown });
        alert(`Fee Settings Saved for ${selectedStudentForFee.name}!\nTotal Fee: ₹${feeStates.totalFee}\nPayment Added: ₹${feeStates.paymentAmount || 0}`);
        handleCloseManageFeeModal();
    };

    // Handle pagination
    const handlePageChange = (page) => {
        if (page && page !== currentPage && page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchFeeStructures(page, filters);
        }
    };

    // Handle logout
    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();

        if (onNavigate) {
            onNavigate('home');
        } else {
            window.location.href = '/';
        }
    };

    // Handle navigation
    // Using Accounts Sidebar items


    // Get user initials
    const getUserInitials = (name) => {
        if (!name || typeof name !== 'string') return 'AC';
        const names = name.trim().split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '₹0.00';
        return '₹' + parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Get year display
    const getYearDisplay = (year) => {
        const yearMap = {
            2001: '1st Year',
            2002: '2nd Year',
            2003: '3rd Year',
            2004: '4th Year'
        };
        return yearMap[year] || (year ? `Year ${year}` : 'N/A');
    };

    // Get semester display
    const getSemesterDisplay = (student) => {
        let sem = student.semester || student.sem || student.current_semester;

        if (!sem || (String(sem).length === 4 && !isNaN(sem) && parseInt(sem) > 1900)) {
            if (filters.semester) {
                sem = filters.semester;
            }
        }

        if (!sem) return 'N/A';

        if (String(sem).length === 4 && !isNaN(sem) && parseInt(sem) > 1900) {
            return 'N/A';
        }

        const semMap = {
            1: '1st Semester',
            2: '2nd Semester',
            3: '3rd Semester',
            4: '4th Semester',
            5: '5th Semester',
            6: '6th Semester',
            7: '7th Semester',
            8: '8th Semester'
        };

        if (semMap[sem]) return semMap[sem];

        if (!isNaN(sem) && sem >= 1 && sem <= 8) {
            return `${sem}${["st", "nd", "rd"][sem - 1] || "th"} Semester`;
        }

        return sem;
    };



    return (
        <Layout role="accounts"
            onNavigate={onNavigate}
            activePage="accounts-fee-manage"
            pageTitle="Fee Management"
            accountsData={accountsData}
        >
            {/* Welcome Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    Fee Management Portal 💰
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Manage fee structures, assign fees to courses and semesters, and maintain comprehensive fee database.
                </p>
            </div>

            {/* Filters and Controls Section */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                {/* Filter Row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    {/* Year Filter - First */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Year
                        </label>
                        <select
                            value={filters.year}
                            onChange={(e) => handleFilterChange('year', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <option value="">All Years</option>
                            {yearOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Department Filter - Second */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Department
                        </label>
                        <select
                            value={filters.department}
                            onChange={(e) => handleFilterChange('department', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <option value="">All Departments</option>
                            {departments.map(dept => (
                                <option key={dept.value} value={dept.value}>{dept.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Batch Filter - Third */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Batch
                        </label>
                        <select
                            value={filters.batch}
                            onChange={(e) => handleFilterChange('batch', e.target.value)}
                            disabled={!filters.department || filterBatchesLoading}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                backgroundColor: (!filters.department) ? '#f3f4f6' : '#fff',
                                cursor: (!filters.department) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <option value="">
                                {!filters.department
                                    ? "Select Dept First"
                                    : filterBatches.length === 0
                                        ? (filterBatchesLoading ? "Loading..." : "No Batches Found")
                                        : "All Batches"}
                            </option>
                            {filterBatches.map(batch => (
                                <option key={batch.id || batch.batch_name || batch} value={batch.batch_name || batch}>
                                    {batch.batch_name || batch}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Semester Filter - Fourth */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Semester
                        </label>
                        <select
                            value={filters.semester}
                            onChange={(e) => handleFilterChange('semester', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <option value="">All Semesters</option>
                            {semesters.map(sem => (
                                <option key={sem.value} value={sem.value}>{sem.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Global Search Input */}
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ position: 'relative', maxWidth: '100%' }}>
                        <input
                            type="text"
                            placeholder="🔍 Search Student by Name, ID, Roll No..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem 0.75rem 3rem',
                                borderRadius: '8px',
                                border: '2px solid #e2e8f0',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#94a3b8',
                            fontSize: '1.1rem'
                        }}>
                            🔍
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #ecf0f1'
                }}>
                    <button
                        onClick={clearFilters}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: '2px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                            color: '#374151',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontWeight: 500
                        }}
                    >
                        🔄 Clear Filters
                    </button>
                </div>

                {/* Stats */}
                <div style={{
                    paddingTop: '1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                }}>
                    {loading ? 'Loading fee structures...' :
                        error ? 'Error loading fee structures' :
                            `Showing ${feeStructures.length} of ${totalCount} fee structures`}
                    {Object.values(filters).some(filter => filter) && !loading && !error && ` (filtered)`}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Fee Structures</p>
                    <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
                    <button
                        onClick={() => fetchFeeStructures(currentPage, filters)}
                        style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Fee Structures Table */}
            {(loading || feeStructures.length > 0) && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    {loading && feeStructures.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '4rem',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}>
                            <div style={{
                                width: '50px',
                                height: '50px',
                                border: '4px solid #e3f2fd',
                                borderTop: '4px solid #238548',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading fee structures...</p>
                        </div>
                    ) : feeStructures.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '4rem 2rem'
                        }}>
                            <h3>No fee structures found</h3>
                        </div>
                    ) : (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.95rem'
                                }}>
                                    <thead>
                                        <tr style={{
                                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                            color: '#ffffff'
                                        }}>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Academic Year</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Course & Semester</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Department</th>
                                            <th style={{ padding: '1rem', textAlign: 'right' }}>Tuition Fee</th>
                                            <th style={{ padding: '1rem', textAlign: 'right' }}>Other Fees</th>
                                            <th style={{ padding: '1rem', textAlign: 'right' }}>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feeStructures.map((feeStructure, index) => (
                                            <tr key={feeStructure.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '1rem', fontWeight: 500 }}>{feeStructure.academic_year}</td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 500 }}>{feeStructure.course_id}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{feeStructure.semester}</div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>{feeStructure.department}</td>
                                                <td style={{ padding: '1rem', textAlign: 'right' }}>{formatCurrency(feeStructure.tuition_fee)}</td>
                                                <td style={{ padding: '1rem', textAlign: 'right' }}>{formatCurrency(feeStructure.other_fees)}</td>
                                                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold', color: '#238548' }}>{formatCurrency(feeStructure.total_amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={{
                                    padding: '1.5rem',
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        Prev
                                    </button>
                                    <span style={{ padding: '0.5rem 1rem' }}>Page {currentPage} of {totalPages}</span>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Student List Section */}
            {(students.length > 0 || studentsLoading) && (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0',
                    marginTop: '2rem',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '1.5rem',
                        borderBottom: '1px solid #f1f5f9'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>
                            🎓 Student List ({students.length})
                        </h3>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '25%' }}>Student Details</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '15%' }}>Roll Number</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '15%' }}>Reg Number</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '20%' }}>Batch</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '10%' }}>Year</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '10%' }}>Semester</th>
                                    <th style={{ padding: '1rem', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '10%' }}>Contact</th>
                                    <th style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontWeight: 600, fontSize: '0.9rem', width: '5%' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsLoading ? (
                                    <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center' }}>Loading students...</td></tr>
                                ) : students.length > 0 ? (
                                    students.map((student) => (
                                        <tr key={student.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <div style={{ width: '36px', height: '36px', backgroundColor: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#166534', fontWeight: 'bold' }}>
                                                        {getUserInitials(student.name)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{student.name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {student.student_id || student.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{student.roll_number || student.roll_no || 'N/A'}</td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{student.reg_number || student.reg_no || 'N/A'}</td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{student.batch_name || 'N/A'}</td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{getYearDisplay(student.year)}</td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{getSemesterDisplay(student)}</td>
                                            <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'left' }}>{student.phone_number || student.phone || 'N/A'}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center', verticalAlign: 'middle' }}>
                                                <button
                                                    onClick={() => handleManageFeeClick(student)}
                                                    style={{ padding: '0.5rem 1rem', backgroundColor: '#238548', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                                                >
                                                    Manage Fee
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center' }}>No students found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AccountsFeeManagement;
